/**
 * ContentOverridesEditor Component
 *
 * Editor for partner-specific content overrides.
 * Allows partners to:
 * - Hide specific fields from the reward workflow
 * - Disable specific validations
 * - Provide custom content values
 */

import React, { useState } from 'react';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { FieldPathPicker } from './FieldPathPicker';
import type {
  PartnerContentOverrides,
  RewardContentOverride,
  CustomContentOverride,
} from '@/app/types/partners';
import { VALIDATION_TYPES, getRewardKeyLabel } from '@/app/types/partners';
import { useRewardMetadata } from '@/app/hooks/useRewardMetadata';
import './ContentOverridesEditor.scss';

interface ContentOverridesEditorProps {
  value: PartnerContentOverrides | null;
  onChange: (value: PartnerContentOverrides | null) => void;
}

export const ContentOverridesEditor: React.FC<ContentOverridesEditorProps> = ({
  value,
  onChange,
}) => {
  const [expandedRewards, setExpandedRewards] = useState<Set<string>>(new Set());
  const { reward_keys, loading: metadataLoading } = useRewardMetadata();

  const overrides = value?.overrides || [];

  // Toggle a reward's expanded state
  const toggleExpanded = (rewardKey: string) => {
    const newExpanded = new Set(expandedRewards);
    if (newExpanded.has(rewardKey)) {
      newExpanded.delete(rewardKey);
    } else {
      newExpanded.add(rewardKey);
    }
    setExpandedRewards(newExpanded);
  };

  // Add a new reward override
  const addRewardOverride = (rewardKey: string) => {
    if (overrides.some((o) => o.reward_key === rewardKey)) return;

    const newOverride: RewardContentOverride = {
      reward_key: rewardKey,
      hide_fields: [],
      hide_validations: [],
      custom_content: [],
    };

    onChange({
      overrides: [...overrides, newOverride],
    });

    // Auto-expand the new override
    setExpandedRewards(new Set([...expandedRewards, rewardKey]));
  };

  // Remove a reward override
  const removeRewardOverride = (rewardKey: string) => {
    onChange({
      overrides: overrides.filter((o) => o.reward_key !== rewardKey),
    });
  };

  // Update a specific reward override
  const updateRewardOverride = (rewardKey: string, updates: Partial<RewardContentOverride>) => {
    onChange({
      overrides: overrides.map((o) =>
        o.reward_key === rewardKey ? { ...o, ...updates } : o
      ),
    });
  };

  // Add a custom content override
  const addCustomContent = (rewardKey: string) => {
    const override = overrides.find((o) => o.reward_key === rewardKey);
    if (!override) return;

    const newCustom: CustomContentOverride = {
      field_path: '',
      value: '',
    };

    updateRewardOverride(rewardKey, {
      custom_content: [...override.custom_content, newCustom],
    });
  };

  // Update a custom content override
  const updateCustomContent = (
    rewardKey: string,
    index: number,
    updates: Partial<CustomContentOverride>
  ) => {
    const override = overrides.find((o) => o.reward_key === rewardKey);
    if (!override) return;

    const newCustomContent = override.custom_content.map((c, i) =>
      i === index ? { ...c, ...updates } : c
    );

    updateRewardOverride(rewardKey, { custom_content: newCustomContent });
  };

  // Remove a custom content override
  const removeCustomContent = (rewardKey: string, index: number) => {
    const override = overrides.find((o) => o.reward_key === rewardKey);
    if (!override) return;

    updateRewardOverride(rewardKey, {
      custom_content: override.custom_content.filter((_, i) => i !== index),
    });
  };

  // Get available reward keys that haven't been added yet (from API metadata)
  const availableRewardKeys = reward_keys.filter(
    (key) => !overrides.some((o) => o.reward_key === key)
  );

  return (
    <div className="content-overrides-editor">
      {/* Add new reward override */}
      {metadataLoading ? (
        <div className="loading-state">Loading available rewards...</div>
      ) : availableRewardKeys.length > 0 ? (
        <div className="add-override-section">
          <label>Add Override for Reward:</label>
          <div className="reward-buttons">
            {availableRewardKeys.map((key) => (
              <button
                key={key}
                type="button"
                className="add-reward-btn"
                onClick={() => addRewardOverride(key)}
              >
                + {getRewardKeyLabel(key)}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {/* Empty state */}
      {overrides.length === 0 && (
        <div className="empty-state">
          <p>No content overrides configured for this partner.</p>
          <p className="empty-hint">
            Add overrides to customize how reward workflows appear for this partner's users.
          </p>
        </div>
      )}

      {/* Reward overrides list */}
      <div className="overrides-list">
        {overrides.map((override) => {
          const isExpanded = expandedRewards.has(override.reward_key);

          return (
            <div key={override.reward_key} className="override-card">
              {/* Header */}
              <div className="override-header" onClick={() => toggleExpanded(override.reward_key)}>
                <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                <span className="reward-name">{getRewardKeyLabel(override.reward_key)}</span>
                <span className="override-summary">
                  {override.hide_fields.length > 0 && (
                    <span className="summary-badge">{override.hide_fields.length} hidden</span>
                  )}
                  {override.hide_validations.length > 0 && (
                    <span className="summary-badge">
                      {override.hide_validations.length} validations off
                    </span>
                  )}
                  {override.custom_content.length > 0 && (
                    <span className="summary-badge">{override.custom_content.length} custom</span>
                  )}
                </span>
                <button
                  type="button"
                  className="remove-override-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeRewardOverride(override.reward_key);
                  }}
                >
                  Remove
                </button>
              </div>

              {/* Content */}
              {isExpanded && (
                <div className="override-content">
                  {/* Hidden Fields */}
                  <div className="override-section">
                    <h5>Hidden Fields</h5>
                    <p className="section-hint">
                      Select fields to hide from the UI for this partner.
                    </p>
                    <FieldPathPicker
                      selectedPaths={override.hide_fields}
                      onChange={(paths) =>
                        updateRewardOverride(override.reward_key, { hide_fields: paths })
                      }
                      label=""
                      placeholder="Click to select fields to hide..."
                    />
                  </div>

                  {/* Disabled Validations */}
                  <div className="override-section">
                    <h5>Disabled Validations</h5>
                    <p className="section-hint">
                      Select validation types to disable for this partner.
                    </p>
                    <div className="validation-checkboxes">
                      {VALIDATION_TYPES.map((validationType) => (
                        <label key={validationType} className="validation-checkbox">
                          <input
                            type="checkbox"
                            checked={override.hide_validations.includes(validationType)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                updateRewardOverride(override.reward_key, {
                                  hide_validations: [...override.hide_validations, validationType],
                                });
                              } else {
                                updateRewardOverride(override.reward_key, {
                                  hide_validations: override.hide_validations.filter(
                                    (v) => v !== validationType
                                  ),
                                });
                              }
                            }}
                          />
                          <span className="validation-name">
                            {validationType.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Custom Content */}
                  <div className="override-section">
                    <h5>Custom Content</h5>
                    <p className="section-hint">
                      Override specific field values with partner-specific content.
                    </p>

                    {override.custom_content.length === 0 && (
                      <p className="no-custom">No custom content defined.</p>
                    )}

                    {override.custom_content.map((custom, index) => (
                      <div key={index} className="custom-content-item">
                        <FieldPathPicker
                          label="Field Path"
                          selectedPaths={custom.field_path ? [custom.field_path] : []}
                          onChange={(paths) =>
                            updateCustomContent(override.reward_key, index, { 
                              field_path: paths[0] || '' 
                            })
                          }
                          placeholder="Select field to override..."
                          singleSelect={true}
                        />
                        <RichTextEditor
                          label="Custom Value"
                          value={custom.value}
                          onChange={(val) =>
                            updateCustomContent(override.reward_key, index, { value: val })
                          }
                          placeholder="Enter the custom content for this field..."
                        />
                        <button
                          type="button"
                          className="remove-custom-btn"
                          onClick={() => removeCustomContent(override.reward_key, index)}
                        >
                          Remove Custom Content
                        </button>
                      </div>
                    ))}

                    <button
                      type="button"
                      className="add-custom-btn"
                      onClick={() => addCustomContent(override.reward_key)}
                    >
                      + Add Custom Content
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ContentOverridesEditor;

